import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { vendorState } from 'state';

import {
  EnumJobTagSpecies,
  JobTagManyDocument,
  useJobTagManyQuery,
} from '../../lib/graphql';

import { filterTagsForSpecies } from './filter-tags-for-species';
import { useJobTagsEnabled } from './use-job-tags-enabled';

export interface JobTagRow {
  _id: string;
  name: string;
  icon: string;
  /** Empty means ALL species — see the `JobTag` model. */
  species: EnumJobTagSpecies[];
  isEnabled: boolean;
  sortOrder: number;
}

/**
 * Loads the active vendor's job tags.
 *
 * Every consumer that needs the whole SET — the settings editor, the
 * assignment pickers, the calendar's tag filter — reads this one query, so a
 * create/update/delete refetch updates all of them at once; hence the shared
 * `refetchQueries` this returns.
 *
 * Surfaces that merely DISPLAY a job's tags don't need this at all: the job
 * itself carries resolved `tags { _id name icon }` from the server relation.
 * @returns the vendor's tags (all and enabled-only), a species filter helper,
 *   the loading and loaded flags, and the refetch descriptor every writer
 *   should pass.
 */
export function useJobTags() {
  const vendor = useRecoilValue(vendorState);
  const vendorId = vendor?._id;
  const jobTagsEnabled = useJobTagsEnabled();

  const { data, loading } = useJobTagManyQuery({
    variables: { filter: { vendor: vendorId } },
    // Don't query at all for a processor without the module — the server
    // would answer, but nothing may render it and the request is wasted.
    skip: !vendorId || !jobTagsEnabled,
  });

  const tags = useMemo<JobTagRow[]>(
    () =>
      sortBy(
        (data?.jobTagMany ?? []).map((t) => ({
          _id: t._id,
          name: t.name,
          icon: t.icon,
          species: (t.species ?? []).filter(
            (s): s is EnumJobTagSpecies => s != null,
          ),
          isEnabled: t.isEnabled,
          sortOrder: t.sortOrder ?? 0,
        })),
        ['sortOrder', 'name'],
      ),
    [data?.jobTagMany],
  );

  const enabledTags = useMemo(() => tags.filter((t) => t.isEnabled), [tags]);

  /** See `filterTagsForSpecies` for the empty-means-all-species rule. */
  const tagsForSpecies = useCallback(
    (species: string | null | undefined) =>
      filterTagsForSpecies(enabledTags, species),
    [enabledTags],
  );

  const refetchQueries = useMemo(
    () => [
      {
        query: JobTagManyDocument,
        variables: { filter: { vendor: vendorId } },
      },
    ],
    [vendorId],
  );

  return {
    vendorId,
    jobTagsEnabled,
    tags,
    enabledTags,
    tagsForSpecies,
    loading,
    // True once the vendor's tags have actually arrived — distinct from
    // `!loading`, which is also true while the query is skipped.
    loaded: !!data?.jobTagMany,
    refetchQueries,
  };
}
